/**
 * The strip over a closed feature's screen that is filled with demo data
 * (#2816): says the numbers are demo, which plan the feature is sold from, and
 * points to the plans — for a role that can open billing — or to the account
 * owner. The same role and shell logic as `TierLockedScreen` (`useTierPlanCta`):
 * no plans link in the Bitrix24 iframe. Before the Pro trial starts it says
 * the trial begins with the next analysis (`ProTrialLockNote`, #2816).
 */
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import type { FeatureFlags } from '@/contexts/FeatureFlagsContext';
import { ProTrialLockNote } from '@/components/ui/ProTrialLockNote';
import { useTierPlanCta } from '@/hooks/useTierPlanCta';
import { PLANS_HREF } from '@/lib/plans-href';

export function LockedDemoBanner({ flag }: { flag: keyof FeatureFlags }) {
  const { t } = useTranslation('common');
  const { plan, canOpenPlans, embedded } = useTierPlanCta(flag);

  return (
    <div
      role="note"
      data-testid="locked-demo-banner"
      className="flex flex-wrap items-center justify-between gap-2 rounded-card border border-border bg-info-dim px-4 py-3"
    >
      <div className="flex items-start gap-2">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium text-info-fg">
            {plan
              ? t('featureGate.demo.title', { plan })
              : t('featureGate.demo.titleGeneric')}
          </p>
          <ProTrialLockNote flag={flag} className="text-xs text-info-fg" />
          {!canOpenPlans && (
            <p className="text-xs text-info-fg">
              {t(
                embedded
                  ? 'featureGate.demo.askOwnerEmbedded'
                  : 'featureGate.demo.askOwner'
              )}
            </p>
          )}
        </div>
      </div>
      {canOpenPlans && (
        <a
          href={PLANS_HREF}
          className="text-sm font-medium text-info-fg underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('featureGate.tier.cta')}
        </a>
      )}
    </div>
  );
}
